// Map an axios/unknown error from the buyer endpoints to a stable `{code, detail}`
// the wizard can branch on. The backend returns `{detail, code}` (4xx) plus, on
// competitor caps, `{currentCount, max}`. Throttle (429) surfaces as THROTTLED.

import axios from "axios";
import type { VbErrorCode } from "@/types/vb";

export interface VbError {
  /** Stable public code from the response body, if present. Typed as the known
   *  union plus an open string (the backend may add codes the UI doesn't map). */
  code?: VbErrorCode | (string & {});
  /** User-facing message. */
  detail: string;
  /** HTTP status, when known. */
  status?: number;
}

/** Longest body string we'll show verbatim. Anything past this is a stray blob
 *  rather than a validation message, and the error banner is one line. */
const MAX_FIELD_MESSAGE = 300;

/** Pull the first human-readable message out of a DRF field-error body.
 *
 *  DRF renders field validation as `{"description": ["…"]}` — no `detail`, no
 *  `code` — and nested serializers one level deeper as
 *  `{"competitors": [{"url": ["…"]}]}`. Neither shape matched the checks in
 *  `extractVbError`, so every field error collapsed into the generic message.
 */
function firstFieldMessage(value: unknown, depth = 0): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed && trimmed.length <= MAX_FIELD_MESSAGE ? trimmed : undefined;
  }
  // A nested serializer error nests four deep before the message
  // (root → "competitors" list → item object → field list → string), so the
  // guard has to clear that while still bounding a pathological payload.
  if (depth >= 6) return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstFieldMessage(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (value && typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const found = firstFieldMessage(item, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

export function extractVbError(err: unknown): VbError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as
      | { detail?: string; code?: string; [k: string]: unknown }
      | undefined;
    if (status === 429) {
      return {
        code: "THROTTLED",
        status,
        detail:
          (data && typeof data.detail === "string" && data.detail) ||
          "You're doing that too fast. Try again in a minute.",
      };
    }
    if (data && (data.detail || data.code)) {
      return {
        code: typeof data.code === "string" ? data.code : undefined,
        status,
        detail:
          typeof data.detail === "string"
            ? data.detail
            : "Something went wrong. Please try again.",
      };
    }
    if (status && status >= 500) {
      return { status, detail: "The server hit an error. Please try again." };
    }
    // Field-level 4xx (no `detail`/`code`): show what the server actually
    // objected to rather than a generic message the user can't act on. Only a
    // structured body qualifies — a bare string response is an error page, not
    // a message for the user, and pasting one into the banner helps nobody.
    const fieldMessage =
      data && typeof data === "object" ? firstFieldMessage(data) : undefined;
    if (fieldMessage) return { status, detail: fieldMessage };
    return { status, detail: "Something went wrong. Please try again." };
  }
  return { detail: "Something went wrong. Please try again." };
}
